import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import api, { updateToken as updateApiToken } from "../services/api";
import throttle from "lodash/throttle";
import { shouldDeferFocusPermissionCheck } from "../utils/filePickerFocus";
import { hasChangedAuthScope, userFromTokenClaims } from "../utils/authSession";
import { productLoginPath, isSecurityRoute } from "../utils/productLogin";

// 🔐 Contexto de Autenticação
const AuthContext = createContext();
export const useAuthContext = () => useContext(AuthContext);

const PUBLIC_ROUTES = [
  "/pedido-oracao", "/testemunho", "/batismo-2025", "/login", "/loginfilter",
  "/recuperar-senha", "/validate-reset-code", "/reset-password",
  "/Security/recuperar-senha", "/Security/validate-reset-code",
  "/Security/reset-password", "/Security/alterar-senha-primeiro-acesso",
  "/alterar-senha-primeiro-acesso", "/conferencia-de-gloria-e-poder",
  "/producao/login", "/compras/fornecedor",
  "/pwa/colaborador", "/pwa/beleza",
  "/eventos/checkin", "/producao/etiqueta-coplac", "/cotacao", "/beleza/contratar",
  "/beleza/contratar/sucesso", "/beleza/vale-presente", "/beleza/vale-presente/sucesso",
];

const isPublicRoute = (pathname) => {
  if (isSecurityRoute(pathname)) return true;
  const path = String(pathname || "").toLowerCase();
  return PUBLIC_ROUTES.some(route => {
    const normalized = route.toLowerCase();
    return path === normalized || path.startsWith(`${normalized}/`);
  });
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [isWarning, setIsWarning] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const isRefreshing = useRef(false);
  const permissionsInFlight = useRef(null);
  const lastFocusPermissionsAt = useRef(0);
  const nextRefreshAttemptAt = useRef(0);
  const refreshRetryTimer = useRef(null);
  const locationRef = useRef(location.pathname);
  const sessionRevision = useRef(0);

  const isTokenValid = (token) => {
    try {
      const decoded = jwtDecode(token);
      return decoded.exp > Math.floor(Date.now() / 1000);
    } catch {
      return false;
    }
  };

  const calculateTimeLeft = (token) => {
    try {
      const decoded = jwtDecode(token);
      return decoded.exp - Math.floor(Date.now() / 1000);
    } catch {
      return 0;
    }
  };

  const persistContextFromUser = (currentUser = {}) => {
    const entidadeId = Object.prototype.hasOwnProperty.call(currentUser, "entidadeId") ? currentUser.entidadeId : currentUser.EntidadeId;
    const unidadeId = Object.prototype.hasOwnProperty.call(currentUser, "unidadeId") ? currentUser.unidadeId : currentUser.UnidadeId;

    for (const [id, keys, headers] of [
      [entidadeId, ["selectedEntidade", "Entidade", "EntidadeId", "entidadeId"], ["Entidade", "EntidadeId"]],
      [unidadeId, ["selectedUnidade", "Unidade", "UnidadeId", "unidadeId"], ["Unidade", "UnidadeId"]],
    ]) {
      keys.forEach((key) => {
        if (id) localStorage.setItem(key, String(id));
        else localStorage.removeItem(key);
      });
      headers.forEach((key) => {
        if (id) api.defaults.headers.common[key] = String(id);
        else delete api.defaults.headers.common[key];
      });
    }
  };


  const logout = useCallback(() => {
    sessionRevision.current += 1;
    permissionsInFlight.current = null;
    lastFocusPermissionsAt.current = 0;
    nextRefreshAttemptAt.current = 0;
    clearTimeout(refreshRetryTimer.current);
    console.group("🔴 Logout iniciado");
    setUser(null);
    setPermissions([]);
    setPermissionsLoading(false);
    setIsAuthenticated(false);
    setTimeLeft(null);
    setIsWarning(false);
    updateApiToken(null);
    localStorage.removeItem("user");
    localStorage.removeItem("permissions");
    sessionStorage.removeItem("senhaAtual");
    sessionStorage.removeItem("pwabeleza:firstAccess");
    localStorage.removeItem("selectedEntidade");
    localStorage.removeItem("selectedUnidade");
    localStorage.removeItem("Entidade");
    localStorage.removeItem("Unidade");
    localStorage.removeItem("EntidadeId");
    localStorage.removeItem("UnidadeId");
    localStorage.removeItem("entidadeId");
    localStorage.removeItem("unidadeId");
    delete api.defaults.headers.common["Authorization"];
    persistContextFromUser({});
    console.log("✅ Logout concluído e dados limpos.");
    console.groupEnd();

    const pathname = locationRef.current;
    const destination = productLoginPath(pathname);
    // Login and security pages are public; logging out there must not break
    // recovery. /loginfilter is not a landing page, despite being public.
    if ((!isPublicRoute(pathname) || pathname === "/loginfilter") && pathname !== destination) {
      navigate(destination, { replace: true });
    }
  }, [navigate]);

  // The JWT identifies the selected context; only the backend computes the
  // current version/module/screen/profile intersection. Never trust browser ACLs.
  const fetchEffectivePermissions = useCallback(async (token, scopedUser) => {
    if (!token || !scopedUser?.unidadeId) return [];
    const response = await api.get("/api/Permissao/efetivas", {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.data?.success || !Array.isArray(response.data.data)) {
      throw new Error(response.data?.message || "Falha ao verificar permissões efetivas.");
    }
    return response.data.data;
  }, []);

  const refreshPermissions = useCallback(() => {
    const token = localStorage.getItem("token");
    if (!token || !isTokenValid(token)) return Promise.resolve();

    const revision = sessionRevision.current;
    // focus e visibilitychange são disparados praticamente juntos. Uma só
    // consulta por token/contexto evita corridas e mudanças de estado inúteis.
    const pending = permissionsInFlight.current;
    if (pending?.token === token && pending.revision === revision) {
      return pending.promise;
    }

    const userBefore = userFromTokenClaims(jwtDecode(token));
    setPermissionsLoading(true);
    const request = fetchEffectivePermissions(token, userBefore)
      .then(effective => {
        if (revision !== sessionRevision.current || localStorage.getItem("token") !== token) return;
        // Nenhuma mudança real de ACL: não publicar outra árvore de permissões.
        const serialized = JSON.stringify(effective);
        if (localStorage.getItem("permissions") !== serialized) {
          localStorage.setItem("permissions", serialized);
          setPermissions(previous => JSON.stringify(previous) === serialized ? previous : effective);
        }
      })
      .catch(error => {
        if (revision === sessionRevision.current && localStorage.getItem("token") === token) {
          // Um 503 não revoga permissões já verificadas nesta sessão.
          // A API sempre valida cada operação no servidor.
          if ([401, 403].includes(error?.response?.status)) {
            localStorage.removeItem("permissions");
            setPermissions(previous => previous.length ? [] : previous);
          }
        }
        console.warn("Não foi possível atualizar as permissões:", error);
      })
      .finally(() => {
        if (permissionsInFlight.current?.promise === request) {
          permissionsInFlight.current = null;
          if (revision === sessionRevision.current) setPermissionsLoading(false);
        }
      });
    permissionsInFlight.current = { token, revision, promise: request };
    return request;
  }, [fetchEffectivePermissions]);

  const updateToken = useCallback(async (novoToken, permissoesDiretas = null, context = {}) => {
    const revision = ++sessionRevision.current;
    lastFocusPermissionsAt.current = 0;
    const previousToken = localStorage.getItem("token");
    const previousUser = JSON.parse(localStorage.getItem("user")) || {};
    const storedPermissions = JSON.parse(localStorage.getItem("permissions")) || [];
    const previousPermissions = Array.isArray(storedPermissions) ? storedPermissions : [];

    try {
      const decoded = jwtDecode(novoToken);
      if (!decoded.exp || decoded.exp <= Math.floor(Date.now() / 1000)) {
        throw new Error("A sessão recebida expirou. Entre novamente.");
      }
      const updatedUser = userFromTokenClaims(decoded, previousUser, context);
      let novasPermissoes = [];

      // Permission requests must use the selected JWT and its context. Publish
      // the user and permissions together only after the request succeeds.
      updateApiToken(novoToken);
      persistContextFromUser(updatedUser);

      // Direct permissions from the login response are not authoritative after
      // selecting/changing units; root/admin also use the same current query.
      novasPermissoes = await fetchEffectivePermissions(novoToken, updatedUser);

      if (revision !== sessionRevision.current) {
        const error = new Error("A sessão mudou durante a seleção do contexto.");
        error.code = "AUTH_SESSION_CHANGED";
        throw error;
      }
      localStorage.setItem("user", JSON.stringify(updatedUser));
      localStorage.setItem("permissions", JSON.stringify(novasPermissoes));
      setUser(updatedUser);
      setPermissions(novasPermissoes);
      setTimeLeft(decoded.exp - Math.floor(Date.now() / 1000));
      setIsWarning(false);
      setIsAuthenticated(true);
      return updatedUser;
    } catch (err) {
      if (revision === sessionRevision.current) {
        updateApiToken(previousToken);
        persistContextFromUser(previousUser);
      }
      console.error("❌ Erro ao atualizar token:", err);
      throw err;
    }
  }, [fetchEffectivePermissions]);

  const refreshToken = async () => {
    if (isRefreshing.current || Date.now() < nextRefreshAttemptAt.current) return;
    const currentToken = localStorage.getItem("token");
    if (!currentToken || !isTokenValid(currentToken)) {
      logout();
      return;
    }
    isRefreshing.current = true;
    try {
      console.group("🔄 Tentando renovar token...");
      const response = await api.post("/api/Auth/refresh-token", { token: currentToken }, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${currentToken}`,
        },
      });

      const novoToken = response.data?.data?.token || response.data?.token;
      if (!novoToken) throw new Error("Token não retornado pelo backend.");

      await updateToken(novoToken);

      nextRefreshAttemptAt.current = 0;
      clearTimeout(refreshRetryTimer.current);
      console.log("✅ Token renovado com sucesso.");
    } catch (error) {
      if (error.code === "AUTH_SESSION_CHANGED") return;
      const status = error?.response?.status;
      // Queda de API / S3 e erros transitórios NÃO invalidam JWT.
      // Mesmo 401/403 no endpoint de refresh pode ser política do endpoint:
      // conservar o JWT enquanto válido e deixar o backend validar operações.
      if (isTokenValid(localStorage.getItem("token"))) {
        console.warn("Renovação temporariamente indisponível; não encerrar sessão:", status || error?.message);
        nextRefreshAttemptAt.current = Date.now() + 30000;
        clearTimeout(refreshRetryTimer.current);
        refreshRetryTimer.current = setTimeout(() => {
          nextRefreshAttemptAt.current = 0;
          refreshToken();
        }, 30000);
      } else {
        logout();
      }
    } finally {
      isRefreshing.current = false;
      console.groupEnd();
    }
  };

  useEffect(() => {
    locationRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    let userData = null;
    try { userData = JSON.parse(localStorage.getItem("user")); } catch { /* no session */ }
    if (!token || !userData || !isTokenValid(token)) {
      logout();
      setLoading(false);
      return;
    }

    const revision = ++sessionRevision.current;
    const restoredUser = userFromTokenClaims(jwtDecode(token), userData);
    localStorage.setItem("user", JSON.stringify(restoredUser));
    localStorage.removeItem("permissions");
    updateApiToken(token);
    persistContextFromUser(restoredUser);
    setUser(restoredUser);
    setPermissions([]);
    setTimeLeft(calculateTimeLeft(token));
    setIsAuthenticated(true);
    // Restore with a fresh database read, even when the JWT and local cache
    // appear unchanged (an administrator may have revoked a screen meanwhile).
    fetchEffectivePermissions(token, restoredUser)
      .then(effective => {
        if (revision !== sessionRevision.current) return;
        localStorage.setItem("permissions", JSON.stringify(effective));
        setPermissions(effective);
      })
      .catch(error => {
        if (revision !== sessionRevision.current) return;
        console.warn("Permissões não recuperadas:", error);
        localStorage.removeItem("permissions");
        setPermissions([]);
      })
      .finally(() => { if (revision === sessionRevision.current) setLoading(false); });
  }, []);

  useEffect(() => {
    // A validação mantém frequência de 1 segundo, mas a SPA só é avisada
    // a cada minuto enquanto não há alerta de expiração.
    const interval = setInterval(() => {
      const token = localStorage.getItem("token");
      if (!token || !isTokenValid(token)) {
        clearInterval(interval);
        logout();
        return;
      }
      const remaining = calculateTimeLeft(token);
      if (remaining <= 0) {
        clearInterval(interval);
        logout();
      } else if (remaining <= 60) {
        setIsWarning(true);
        setTimeLeft(remaining);
      } else {
        setIsWarning(wasWarning => wasWarning ? false : wasWarning);
        setTimeLeft(previous => previous === null || Math.floor(previous / 60) !== Math.floor(remaining / 60)
          ? remaining : previous);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [logout]);

  useEffect(() => {
    let renewalTimeout;

    const scheduleTokenRefresh = () => {
      const token = localStorage.getItem("token");
      if (!token || !isTokenValid(token)) return;

      const timeRemaining = calculateTimeLeft(token);
      const renewIn = (timeRemaining - 120) * 1000;

      if (renewIn > 0) {
        clearTimeout(renewalTimeout);
        renewalTimeout = setTimeout(() => {
          refreshToken();
        }, renewIn);
      } else {
        refreshToken();
      }
    };

    const resetTimerOnActivity = throttle(scheduleTokenRefresh, 5000);

    window.addEventListener("mousemove", resetTimerOnActivity);
    window.addEventListener("click", resetTimerOnActivity);
    window.addEventListener("keydown", resetTimerOnActivity);

    scheduleTokenRefresh();

    return () => {
      clearTimeout(renewalTimeout);
      clearTimeout(refreshRetryTimer.current);
      window.removeEventListener("mousemove", resetTimerOnActivity);
      window.removeEventListener("click", resetTimerOnActivity);
      window.removeEventListener("keydown", resetTimerOnActivity);
    };
  }, []);

  useEffect(() => {
    const onFocus = () => {
      // Retornar a uma aba não reinicializa a sessão. Evitar consultas
      // duplicadas de focus/visibilitychange e o retorno do file picker.
      if (shouldDeferFocusPermissionCheck()) return;
      const token = localStorage.getItem("token");
      if (!token || !isTokenValid(token)) return;
      const now = Date.now();
      if (now - lastFocusPermissionsAt.current < 15000) return;
      lastFocusPermissionsAt.current = now;
      void refreshPermissions();
    };
    const onPermissionChanged = () => {
      // Alteração explícita de permissões: não aplicar cooldown do foco.
      if (localStorage.getItem("token")) void refreshPermissions();
    };
    const onVisibility = () => { if (document.visibilityState === "visible") onFocus(); };
    window.addEventListener("focus", onFocus);
    window.addEventListener("permissions:updated", onPermissionChanged);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("permissions:updated", onPermissionChanged);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [refreshPermissions]);

  const login = (token, userData, userPermissions = []) => {
    try {
      const decoded = jwtDecode(token);
      if (!decoded.exp) return;
      sessionRevision.current += 1;
      const normalizedUser = userFromTokenClaims(decoded, userData);

      updateApiToken(token);
      localStorage.setItem("user", JSON.stringify(normalizedUser));
      localStorage.setItem("permissions", JSON.stringify(userPermissions));
      persistContextFromUser(normalizedUser);
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      setUser(normalizedUser);
      setPermissions(userPermissions);
      setTimeLeft(decoded.exp - Math.floor(Date.now() / 1000));
      setIsWarning(false);
      setIsAuthenticated(true);
    } catch (err) {
      console.error("❌ Erro ao decodificar token no login:", err);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        setUser,
        permissions,
        setPermissions,
        permissionsLoading,
        refreshPermissions,
        loading,
        timeLeft,
        isWarning,
        login,
        logout,
        updateToken,
        token: localStorage.getItem("token"),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
